/**
 * REPAYMENT CENTER (UF-06). One place for every repayment across every loan.
 *
 * A borrower with more than one loan should never hunt through loan pages to pay. This consolidates
 * upcoming payments, the ways to pay (auto debit, manual, partial, extra, early settlement), and the
 * history, and offers a restructure request when things get tight, before a missed payment, not after.
 *
 * Preview: payments are simulated until lending activates.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type FinanceLoan } from '@/lib/api';
import { PageHeader, SectionTitle } from '@/components/dash';
import { ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { fmtDate } from '@/lib/format';

const num = (s: string) => Number(s.replace(/,/g, '')) || 0;

export default function RepaymentCenter() {
  const [loans, setLoans] = useState<FinanceLoan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paid, setPaid] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setLoans((await api.finance.loans()).loans);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load your repayments.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const due = useMemo(
    () => (loans ?? []).filter((l) => l.status === 'performing' || l.status === 'active'),
    [loans],
  );
  const totalDue = useMemo(
    () => due.filter((l) => !paid[l.id]).reduce((s, l) => s + num(l.monthly), 0),
    [due, paid],
  );

  async function pay(l: FinanceLoan) {
    setBusy(l.id);
    try {
      await api.finance.repay(l.id, l.monthly);
      setPaid((p) => ({ ...p, [l.id]: true }));
    } catch {
      /* surfaced on reload */
    } finally {
      setBusy(null);
    }
  }

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!loans) return <LoadingCard lines={4} />;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/credit"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Credit
      </Link>

      <PageHeader title="Repayment center" subtitle="Every repayment, in one place." />

      {/* TOTAL DUE. */}
      <Reveal>
        <section className="rounded-card border border-border bg-surface p-5 shadow-card">
          <p className="text-micro text-ink-subtle">Due this cycle</p>
          <p className="tabular text-3xl font-semibold text-ink">
            KES{' '}
            {totalDue.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
          <p className="mt-1 text-micro text-ink-subtle">
            Across {due.length} active loan{due.length === 1 ? '' : 's'}.
          </p>
        </section>
      </Reveal>

      {/* UPCOMING PAYMENTS. */}
      <section>
        <SectionTitle>Upcoming payments</SectionTitle>
        {due.length === 0 ? (
          <p className="rounded-card border border-dashed border-border bg-surface p-6 text-center text-sm text-ink-muted">
            Nothing due right now.
          </p>
        ) : (
          <ul className="space-y-2.5">
            {due.map((l) => (
              <li
                key={l.id}
                className="rounded-card border border-border bg-surface p-4 shadow-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{l.asset}</p>
                    <p className="text-micro text-ink-subtle">
                      {l.provider} · due {fmtDate(l.nextDue)}
                    </p>
                  </div>
                  <p className="tabular shrink-0 text-sm font-semibold text-ink">KES {l.monthly}</p>
                </div>
                {paid[l.id] ? (
                  <p className="mt-3 inline-flex items-center gap-1.5 text-micro font-medium text-accent">
                    <CheckIcon /> Payment recorded
                  </p>
                ) : (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => pay(l)}
                      disabled={busy === l.id}
                      className="flex-1 rounded-field bg-primary py-2.5 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-60"
                    >
                      {busy === l.id ? 'Paying...' : 'Pay now'}
                    </button>
                    <Link
                      to={`/credit/loans/${l.id}`}
                      className="rounded-field border border-border px-3.5 py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
                    >
                      Options
                    </Link>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* WAYS TO PAY. */}
      <section>
        <SectionTitle>Ways to pay</SectionTitle>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {[
            'Auto debit',
            'Manual payment',
            'Partial payment',
            'Extra payment',
            'Early settlement',
            'Download statement',
          ].map((w) => (
            <div
              key={w}
              className="rounded-card border border-border bg-surface p-3 text-sm font-medium text-ink shadow-card"
            >
              {w}
            </div>
          ))}
        </div>
      </section>

      <div className="rounded-card border border-border bg-surface p-4 shadow-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-ink">Struggling with a payment?</p>
            <p className="text-micro text-ink-subtle">
              Request a restructure before a payment is missed.
            </p>
          </div>
          <button className="shrink-0 rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken">
            Request restructure
          </button>
        </div>
      </div>

      <p className="text-center text-xs text-ink-subtle">
        Preview: payments are simulated until lending activates.
      </p>
    </div>
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="m5 13 4 4L19 7" />
    </svg>
  );
}
